import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Loader2, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { submitFeedback } from '../lib/api';

/**
 * Feedback form (`/feedback`). Sends a message to the `feedback` collection.
 * Admins receive a consolidated daily digest via direct_notifications.
 */
const Feedback = () => {
  const { user, loading: authLoading, openAuthModal: login } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (message.trim().length < 3) {
      setErr('Please share a bit more before sending.');
      return;
    }
    setSubmitting(true);
    try {
      // Attach the signed-in user's email automatically so admins can
      // still follow up. Anonymous senders submit with a null email.
      await submitFeedback({ message: message.trim(), email: user?.email || null });
      setSent(true);
      setMessage('');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || 'Could not send feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-6" data-testid="feedback-page">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="feedback-title">Send feedback</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Bugs, feature ideas, moments that moved you — anything. We read everything.
      </p>

      {/*
        Auth gate — feedback submission is signed-in-only so admins can
        always attribute an entry to a real account. Anonymous visitors
        see a Sign in prompt in place of the form. Matches the pattern
        used by AddPrayer / ManagePrayers.
      */}
      {authLoading ? (
        <div className="p-10 text-center text-zinc-500" data-testid="feedback-loading">Loading…</div>
      ) : !user ? (
        <div
          className="p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 text-center"
          data-testid="feedback-signin-gate"
        >
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Please sign in to send feedback.
          </p>
          <button
            onClick={login}
            data-testid="feedback-signin-btn"
            className="h-9 px-4 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium"
          >
            Sign in
          </button>
        </div>
      ) : sent ? (
        <div
          className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 flex items-center gap-3"
          data-testid="feedback-thanks"
        >
          <Check size={20} />
          <div>
            <div className="font-semibold">Thanks — got it.</div>
            <div className="text-sm">Your feedback landed with the team.</div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="fb-msg" className="block text-sm font-medium mb-1">Your feedback</label>
            <textarea
              id="fb-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="feedback-message-input"
              rows={6}
              maxLength={2000}
              placeholder="Enter feedback here..."
              className="w-full p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-vertical"
            />
            <div className="text-xs text-zinc-400 mt-1">{message.length}/2000</div>
          </div>
          {err && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm" data-testid="feedback-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || message.trim().length < 3}
            data-testid="feedback-submit-btn"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <MessageSquare size={16} />}
            Send feedback
          </button>
        </form>
      )}

      <div className="mt-6 text-xs text-zinc-500">
        For urgent moderation concerns, please use <Link to="/report-misuse" className="text-amber-600 hover:underline">Report Misuse</Link>.
      </div>
    </div>
  );
};

export default Feedback;
